import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import CloudDoneRoundedIcon from "@mui/icons-material/CloudDoneRounded";
import CloudOffRoundedIcon from "@mui/icons-material/CloudOffRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import LaptopRoundedIcon from "@mui/icons-material/LaptopRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { getApiError } from "../api/errors";
import {
  enrollOfflinePosDevice,
  getOfflinePosCapability,
  listOfflinePosDevices,
  refreshOfflinePosGrant,
  revokeOfflinePosDevice,
} from "../api/offlinePos";
import { useAuth } from "../context/AuthContext";
import { useConnectivity } from "../context/ConnectivityContext";
import {
  defaultOfflinePosDeviceName,
  detectOfflinePosPlatform,
  offlinePosUserAgent,
} from "../offline/deviceIdentity";
import { offlineStorageSupported } from "../offline/db";
import {
  getOfflineSnapshotMetadata,
  getOfflineSnapshotStatusMetadata,
  refreshOfflinePosSnapshot,
} from "../offline/catalog";
import { getOfflineSaleOperationalStatus } from "../offline/sales";
import {
  getOfflineDevice,
  getOfflinePosSession,
  isOfflineGrantUsable,
  markOfflineDeviceRevokedLocally,
  markOfflineDeviceServerFailure,
  saveOfflineDeviceEnrollment,
  saveOfflinePosSession,
} from "../offline/session";

function dateLabel(value, fallback = "Never") {
  if (!value) return fallback;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? fallback : parsed.toLocaleString();
}

function expiresLabel(value, t) {
  if (!value) return t("offlineDevice.noGrant");
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? t("offlineDevice.unknownExpiry") : t("offlineDevice.grantUntil", { date: parsed.toLocaleString() });
}

export default function OfflinePosDeviceCard() {
  const { t } = useTranslation();
  const { selectedWorkspace, selectedStore, canManage } = useAuth();
  const { state, isOnline } = useConnectivity();
  const workspaceId = selectedWorkspace?.public_id || "";
  const storeId = selectedStore?.public_id || "";

  const [capability, setCapability] = useState(null);
  const [device, setDevice] = useState(null);
  const [session, setSession] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [snapshotStatus, setSnapshotStatus] = useState(null);
  const [serverDevices, setServerDevices] = useState([]);
  const [deviceName, setDeviceName] = useState(() => defaultOfflinePosDeviceName());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const loadLocal = useCallback(async () => {
    if (!workspaceId || !storeId || !offlineStorageSupported()) {
      setDevice(null);
      setSession(null);
      setSnapshot(null);
      setSnapshotStatus(null);
      return;
    }
    const [localDevice, localSession, localSnapshot, localSnapshotStatus] = await Promise.all([
      getOfflineDevice(workspaceId, storeId),
      getOfflinePosSession(workspaceId, storeId),
      getOfflineSnapshotMetadata(workspaceId, storeId),
      getOfflineSnapshotStatusMetadata(workspaceId, storeId),
    ]);
    setDevice(localDevice);
    setSession(localSession);
    setSnapshot(localSnapshot);
    setSnapshotStatus(localSnapshotStatus);
  }, [storeId, workspaceId]);

  const loadCapability = useCallback(async () => {
    if (!isOnline || !workspaceId || !storeId) return;
    try {
      const response = await getOfflinePosCapability();
      setCapability(response.data);
    } catch (requestError) {
      setError(getApiError(requestError, t("offlineDevice.capabilityError")));
    }
  }, [isOnline, storeId, workspaceId, t]);

  const loadServerDevices = useCallback(async () => {
    if (!canManage || !isOnline || !workspaceId || !storeId) {
      setServerDevices([]);
      return;
    }
    try {
      const response = await listOfflinePosDevices();
      setServerDevices(Array.isArray(response.data) ? response.data : []);
    } catch (requestError) {
      setError(getApiError(requestError, t("offlineDevice.devicesError")));
    }
  }, [canManage, isOnline, storeId, workspaceId, t]);

  useEffect(() => {
    setError("");
    setNotice("");
    loadLocal().catch((storageError) => setError(storageError.message));
  }, [loadLocal]);

  useEffect(() => {
    loadCapability();
    loadServerDevices();
  }, [loadCapability, loadServerDevices]);

  const grantReady = useMemo(() => isOfflineGrantUsable(session), [session]);
  const snapshotReady = Boolean(
    snapshot?.snapshot_id
    && snapshot?.device_public_id === device?.device_public_id,
  );
  const offlineReady = Boolean(
    device?.status === "active"
    && grantReady
    && snapshotReady,
  );

  const storeGrant = async (data) => {
    await saveOfflinePosSession({
      workspacePublicId: workspaceId,
      storePublicId: storeId,
      devicePublicId: data.device.public_id,
      grant: data.grant,
      grantExpiresAt: data.grant_expires_at,
      identity: data.identity,
    });
  };

  const enableOfflinePos = async () => {
    if (!canManage || !isOnline || busy) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const response = await enrollOfflinePosDevice({
        name: deviceName.trim() || defaultOfflinePosDeviceName(),
        platform: detectOfflinePosPlatform(),
        user_agent: offlinePosUserAgent(),
      });
      const data = response.data;
      await saveOfflineDeviceEnrollment({
        workspacePublicId: workspaceId,
        storePublicId: storeId,
        device: data.device,
        deviceSecret: data.device_secret,
      });
      await storeGrant(data);
      let synced = false;
      try {
        await refreshOfflinePosSnapshot(workspaceId, storeId);
        synced = true;
      } catch (snapshotError) {
        setError(getApiError(
          snapshotError,
          t("offlineDevice.firstSyncError"),
        ));
      }
      await Promise.all([loadLocal(), loadServerDevices()]);
      setNotice(synced
        ? t("offlineDevice.enrolledReady")
        : t("offlineDevice.enrolledNeedsSync"));
    } catch (requestError) {
      setError(getApiError(requestError, t("offlineDevice.enrollError")));
    } finally {
      setBusy(false);
    }
  };

  const renewGrant = async () => {
    if (!device?.device_public_id || !device?.device_secret || !isOnline || busy) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const response = await refreshOfflinePosGrant(device.device_public_id, device.device_secret);
      await storeGrant(response.data);
      await refreshOfflinePosSnapshot(workspaceId, storeId);
      await Promise.all([loadLocal(), loadServerDevices()]);
      setNotice(t("offlineDevice.grantRefreshed"));
    } catch (requestError) {
      const status = Number(requestError?.response?.status || 0);
      if ([403, 404, 409].includes(status)) {
        await markOfflineDeviceServerFailure({
          workspacePublicId: workspaceId,
          storePublicId: storeId,
          httpStatus: status,
          message: getApiError(requestError, t("offlineDevice.grantRejected")),
        });
        await loadLocal();
      }
      setError(getApiError(requestError, t("offlineDevice.grantError")));
    } finally {
      setBusy(false);
    }
  };

  const syncCatalog = async () => {
    if (!device?.device_public_id || !grantReady || !isOnline || busy) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await refreshOfflinePosSnapshot(workspaceId, storeId);
      await loadLocal();
      setNotice(t("offlineDevice.catalogSynced"));
    } catch (requestError) {
      setError(getApiError(requestError, t("offlineDevice.catalogError")));
    } finally {
      setBusy(false);
    }
  };

  const revokeDevice = async (target) => {
    const publicId = target?.public_id || target?.device_public_id;
    if (!canManage || !publicId || !isOnline || busy) return;
    const isThisBrowser = publicId === device?.device_public_id;
    const targetName = target?.name || t("offlineDevice.thisDevice");
    if (isThisBrowser) {
      const operational = await getOfflineSaleOperationalStatus(workspaceId, storeId);
      const unsynced = operational.sales.filter((sale) => (
        sale.device_public_id === device?.device_public_id
        && ["PENDING", "SYNCING"].includes(sale.status)
      )).length;
      if (unsynced > 0) {
        setError(
          t("offlineDevice.pendingSalesWarning", { count: unsynced }),
        );
        return;
      }
    }
    const warning = isThisBrowser
      ? t("offlineDevice.revokeThisConfirm")
      : t("offlineDevice.revokeOtherConfirm", { name: targetName });
    if (!window.confirm(warning)) return;

    setBusy(true);
    setError("");
    setNotice("");
    try {
      await revokeOfflinePosDevice(publicId);
      if (isThisBrowser) {
        await markOfflineDeviceRevokedLocally({
          workspacePublicId: workspaceId,
          storePublicId: storeId,
          message: t("offlineDevice.revokedFromAbos"),
        });
        await loadLocal();
      }
      await loadServerDevices();
      setNotice(isThisBrowser
        ? t("offlineDevice.revokedThis")
        : t("offlineDevice.revokedOther", { name: targetName }));
    } catch (requestError) {
      setError(getApiError(requestError, t("offlineDevice.revokeError")));
    } finally {
      setBusy(false);
    }
  };

  if (!offlineStorageSupported()) {
    return (
      <Card sx={{ borderRadius: 4 }}>
        <CardContent sx={{ p: 2.6 }}>
          <Alert severity="error">{t("offlineDevice.indexedDbRequired")}</Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ borderRadius: 4 }}>
      <CardContent sx={{ p: 2.6 }}>
        <Stack direction="row" spacing={1.2} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1.1} alignItems="center">
            <LaptopRoundedIcon color="primary" />
            <Typography variant="h6">{t("offlineDevice.title")}</Typography>
          </Stack>
          <Chip
            size="small"
            color={offlineReady ? "success" : device?.status === "active" ? "warning" : device ? "error" : "default"}
            icon={offlineReady ? <CloudDoneRoundedIcon /> : <CloudOffRoundedIcon />}
            label={offlineReady
              ? t("offlineDevice.ready")
              : device?.status === "active"
                ? t("offlineDevice.syncNeeded")
                : device
                  ? t("offlineDevice.deviceStatus", { status: device.status })
                  : t("offlineDevice.notEnrolled")}
          />
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.8 }}>
          {t("offlineDevice.description")}
        </Typography>

        {!isOnline && (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            {t("offlineDevice.serverState", { state })}
          </Alert>
        )}
        {capability && !capability.enabled && (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            {t("offlineDevice.disabledByServer")}
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
        {notice && <Alert severity="success" sx={{ mt: 1.5 }}>{notice}</Alert>}

        {device ? (
          <Stack spacing={1.1} sx={{ mt: 1.7 }}>
            <Typography fontWeight={900}>{device.name}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
              {device.device_public_id}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {expiresLabel(session?.grant_expires_at, t)}
            </Typography>
            {device.status !== "active" && (
              <Alert severity="error">
                {t("offlineDevice.blockedStatus", { status: device.status })}
                {device.authorization_error ? ` ${t("offlineDevice.serverReason", { reason: device.authorization_error })}` : ""}
              </Alert>
            )}
            <Typography variant="body2" color="text.secondary">
              {snapshotStatus?.cached_at
                ? t("offlineDevice.catalogCached", { date: dateLabel(snapshotStatus.cached_at), products: Number(snapshotStatus.counts?.products || 0).toLocaleString(), inventory: Number(snapshotStatus.counts?.inventory || 0).toLocaleString(), recovery: snapshot ? "" : t("offlineDevice.recoveryOnly") })
                : t("offlineDevice.noSnapshot")}
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <Button
                variant="outlined"
                startIcon={busy ? <CircularProgress size={16} /> : <RefreshRoundedIcon />}
                disabled={!isOnline || busy || capability?.enabled !== true || device.status !== "active"}
                onClick={renewGrant}
              >
                {t("offlineDevice.refreshGrant")}
              </Button>
              <Button
                variant="contained"
                startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <CloudDoneRoundedIcon />}
                disabled={!isOnline || busy || !grantReady || capability?.enabled !== true || device.status !== "active"}
                onClick={syncCatalog}
              >
                {t("offlineDevice.syncCatalog")}
              </Button>
              {canManage && (
                <Button
                  color="error"
                  startIcon={<DeleteOutlineRoundedIcon />}
                  disabled={!isOnline || busy || device.status !== "active"}
                  onClick={() => revokeDevice(device)}
                >
                  {t("offlineDevice.revokeDevice")}
                </Button>
              )}
              {canManage && device.status !== "active" && (
                <Button
                  variant="contained"
                  startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <LaptopRoundedIcon />}
                  disabled={!isOnline || busy || capability?.enabled !== true}
                  onClick={enableOfflinePos}
                >
                  {t("offlineDevice.enrollReplacement")}
                </Button>
              )}
            </Stack>
          </Stack>
        ) : (
          <Stack spacing={1.2} sx={{ mt: 1.7 }}>
            {canManage ? (
              <>
                <TextField
                  size="small"
                  label={t("offlineDevice.deviceName")}
                  value={deviceName}
                  onChange={(event) => setDeviceName(event.target.value)}
                  inputProps={{ maxLength: 120 }}
                />
                <Button
                  variant="contained"
                  startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <LaptopRoundedIcon />}
                  disabled={!isOnline || busy || capability?.enabled !== true}
                  onClick={enableOfflinePos}
                >
                  {t("offlineDevice.enableThisBrowser")}
                </Button>
              </>
            ) : (
              <Alert severity="info">{t("offlineDevice.managerRequired")}</Alert>
            )}
          </Stack>
        )}

        {canManage && serverDevices.length > 0 && (
          <>
            <Divider sx={{ my: 2.2 }} />
            <Typography fontWeight={900}>{t("offlineDevice.storeDevices")}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4, mb: 1.2 }}>
              {t("offlineDevice.revokeHelp")}
            </Typography>
            <Stack spacing={1}>
              {serverDevices.map((serverDevice) => {
                const isThisBrowser = serverDevice.public_id === device?.device_public_id;
                const active = serverDevice.status === "active" && !serverDevice.revoked_at;
                return (
                  <Stack
                    key={serverDevice.public_id}
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    alignItems={{ xs: "stretch", sm: "center" }}
                    justifyContent="space-between"
                    sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2.5, p: 1.25 }}
                  >
                    <Stack spacing={0.2} sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={0.7} alignItems="center" flexWrap="wrap">
                        <Typography fontWeight={850}>{serverDevice.name}</Typography>
                        {isThisBrowser && <Chip size="small" label={t("offlineDevice.thisBrowser")} color="primary" variant="outlined" />}
                        <Chip
                          size="small"
                          label={active ? t("common.active") : t("offlineDevice.revoked")}
                          color={active ? "success" : "default"}
                          variant="outlined"
                        />
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
                        {serverDevice.public_id}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("offlineDevice.serverDeviceDates", { grant: dateLabel(serverDevice.last_grant_at, t("offlineDevice.never")), enrolled: dateLabel(serverDevice.created_at, t("offlineDevice.unknown")) })}
                      </Typography>
                    </Stack>
                    {active && (
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteOutlineRoundedIcon />}
                        disabled={!isOnline || busy}
                        onClick={() => revokeDevice(serverDevice)}
                      >
                        {t("offlineDevice.revoke")}
                      </Button>
                    )}
                  </Stack>
                );
              })}
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  );
}
